import React from "react";
import { Link } from "react-router-dom";
import {
  getDomain,
  getPillar,
  getObservation,
  getBuilding,
  getArtifact,
} from "@/data/content";

/**
 * RelatedGraph, cross-entity related-content module for detail pages.
 * Renders up to four columns: Observations, Building, Artifacts, Pillar.
 * Any column with zero entries is omitted.
 *
 * Props: {
 *   observations?: string[]  // slugs
 *   building?: string[]      // slugs
 *   artifacts?: string[]     // slugs
 *   pillar?: string          // pillar slug
 *   fieldNotes?: object[]    // pre-resolved field notes
 * }
 */
export const RelatedGraph = ({
  observations = [],
  building = [],
  artifacts = [],
  pillar = null,
  fieldNotes = [],
  className = "",
  eyebrow = "Threads",
  title = "Follow the thread.",
}) => {
  const obs = observations.map(getObservation).filter(Boolean);
  const proj = building.map(getBuilding).filter(Boolean);
  const arts = artifacts.map(getArtifact).filter(Boolean);
  const p = pillar ? getPillar(pillar) : null;

  const hasAny = obs.length + proj.length + arts.length + fieldNotes.length + (p ? 1 : 0) > 0;
  if (!hasAny) return null;

  const columns = [];
  if (obs.length) columns.push({ key: "observations", title: "Related Observations", items: obs.map(o => ({ to: `/thinking/${o.slug}`, label: o.headline, meta: getDomain(o.domain)?.name })) });
  if (fieldNotes.length) columns.push({ key: "field-notes", title: "Field Notes", items: fieldNotes.map(f => ({ to: `/thinking?domain=${f.domain}`, label: f.text, meta: getDomain(f.domain)?.name })) });
  if (proj.length) columns.push({ key: "building", title: "Where It Shows Up", items: proj.map(b => ({ to: `/building/${b.slug}`, label: b.name, meta: b.category })) });
  if (arts.length) columns.push({ key: "artifacts", title: "Related Artifacts", items: arts.map(a => ({ to: `/artifacts/${a.slug}`, label: a.title, meta: a.era })) });
  if (p) columns.push({ key: "pillar", title: "Under the Lens Of", items: [{ to: `/philosophy#pillar-${p.slug}`, label: p.name, meta: `Pillar ${p.number}` }] });

  const colClass = ["", "md:grid-cols-1", "md:grid-cols-2", "md:grid-cols-3", "md:grid-cols-4"][Math.min(columns.length, 4)];

  return (
    <section
      data-testid="related-graph"
      className={`bg-ink-surface border-t border-ink-line ${className}`}
      aria-label="Related content"
    >
      <div className="container-edge py-20 md:py-24">
        <div className="mb-12 md:mb-14">
          <p className="eyebrow-gold mb-4">{eyebrow}</p>
          <h2 className="display-serif text-2xl md:text-4xl text-paper-primary text-balance">{title}</h2>
        </div>

        <div className={`grid grid-cols-1 ${colClass} gap-px bg-ink-line`}>
          {columns.map((col) => (
            <div key={col.key} data-testid={`related-col-${col.key}`} className="bg-ink-surface p-6 md:p-8">
              <p className="eyebrow mb-5">{col.title}</p>
              <ul className="space-y-4">
                {col.items.map((it, i) => (
                  <li key={`${col.key}-${i}`}>
                    <Link
                      to={it.to}
                      data-testid={`related-${col.key}-${i}`}
                      className="group block"
                    >
                      {it.meta && (
                        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-paper-mute mb-1">{it.meta}</p>
                      )}
                      <p className="font-serif text-base md:text-lg text-paper-primary group-hover:text-gold-warm transition-colors duration-300 leading-snug text-balance">
                        {it.label}
                      </p>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default RelatedGraph;
